import "leaflet/dist/leaflet.css";
import L from "leaflet";
import type { ComponentPropsWithoutRef } from "react";
import { useCallback } from "react";
import { MapContainer, TileLayer } from "react-leaflet";

// Fix Leaflet's broken default marker icons when bundled with Vite.
// Vite processes assets differently from webpack, so Leaflet's internal
// URL resolution for marker images fails without this override.
import iconUrl from "leaflet/dist/images/marker-icon.png?url";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png?url";
import shadowUrl from "leaflet/dist/images/marker-shadow.png?url";

// Step 1: Remove Leaflet's internal _getIconUrl method so it does not override
// the merged options below. This method is not in Leaflet's public TypeScript
// types; the `as any` cast is expected and acceptable here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
// Step 2: Provide the correct asset URLs via Vite's ?url import syntax.
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

interface Props extends ComponentPropsWithoutRef<typeof MapContainer> {
  center: [number, number];
  zoom: number;
  className?: string;
  children?: React.ReactNode;
  /** When true, no default OSM tile layer is rendered (caller manages tiles). */
  noDefaultTile?: boolean;
}

export default function OsmMap({
  center,
  zoom,
  className,
  children,
  noDefaultTile = false,
  ...rest
}: Props) {
  // Remove Leaflet's default attribution prefix, which ships a "Leaflet 🇺🇦"
  // string with a Ukraine flag since Leaflet 1.8. We keep the mandatory
  // tile-provider attributions (OSM/Esri/CARTO) — those come from each
  // TileLayer's `attribution` prop and are untouched. Leaflet's `MapOptions`
  // type only allows `attributionControl: boolean`, so instead of passing
  // `{ prefix: false }` we grab the map instance on mount and call
  // `setPrefix(false)` on its attribution control. Do NOT disable the
  // attribution control entirely — that would drop the legally-required
  // tile attribution too.
  const removeAttributionPrefix = useCallback((map: L.Map | null) => {
    map?.attributionControl?.setPrefix(false);
  }, []);

  return (
    <MapContainer
      ref={removeAttributionPrefix}
      center={center}
      zoom={zoom}
      className={className}
      /* Stated explicitly rather than relying on Leaflet's default being true:
         wheel-zoom is behaviour every map in this app depends on, and an
         implicit default is invisible if a future Leaflet release or a
         `MapContainer` option change flips it. Declared BEFORE `{...rest}` so a
         caller can still opt out per map. */
      scrollWheelZoom
      {...rest}
    >
      {!noDefaultTile && (
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
      )}
      {children}
    </MapContainer>
  );
}
